import { NavLink, useLocation } from "react-router";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import { CustomerName } from "@/shared/components/domain/customer/customer-name";
import { ServiceObjectName } from "@/shared/components/domain/customer/service-object-name";
import { ProcessNumber } from "@/shared/components/domain/processes/process-number";
import { InspectionReportNumber } from "@/shared/components/domain/inspection/inspection-report-number";
import { Process } from "@/services/backend/processes/process/process";
import { ArtefactKind } from "@/services/backend/artefacts/kind";
import { artefactLabel } from "@/shared/components/enum-labels";

/**
 * BreadcrumbsToArtefact is a component attempting to build breadcrumbs
 * for a process page leading to an artefact. The breadcrumbs are derived
 * from the current location, but not based on it.
 * BreadcrumbsToArtefact will attempt to build a list of breadcrumbs
 * in this format: {customerName}-{processNumber (serviceObject)}-{artefactKind}-{artefactNumber}.
 * BreadcrumbsToArtefact will further rely on multiple conventions:
 * - artefact id route parameters are named e.g. "offerId" for an offer artefact
 * - a customer page exists at /customers/{customerId}
 * - process routes are designed as {prefix}/{processId}/{artefactKind (plural)}/{artefactId}
 * - the translation function can translate textual route segments
 * BreadcrumbsToArtefact is a sensible default, but may be customized as needed.
 */
export function BreadcrumbsToArtefact({ process }: { process: Process }) {
  const location = useLocation();

  // extract the path prefix for everything before the process id
  const idx = location.pathname.indexOf(process.id);
  const prefix = location.pathname.slice(0, idx);

  // extract the artefact kind path segment, if it exists
  const fullArtefactPath = location.pathname.slice(idx + process.id.length + 1);
  const artefactKind = fullArtefactPath.split("/")[0];
  const reportId = fullArtefactPath.split("/")[1];

  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem className="hidden md:block">
          <BreadcrumbLink asChild>
            <NavLink to={`/base-data/customers/${process.customerId}`}>
              <CustomerName customerId={process.customerId} />
            </NavLink>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator className="hidden md:block" />
        <BreadcrumbItem className="hidden md:block">
          <BreadcrumbLink asChild>
            <NavLink to={`${prefix}${process.id}`}>
              <span className="flex items-baseline gap-1 truncate font-mono font-bold">
                <ProcessNumber processId={process.id} />
                <span className="text-muted-foreground truncate font-sans font-normal">
                  —{" "}
                  <ServiceObjectName
                    serviceObjectId={process.serviceObjectId}
                  />
                </span>
              </span>
            </NavLink>
          </BreadcrumbLink>
        </BreadcrumbItem>
        {artefactKind && (
          <>
            <BreadcrumbSeparator className="hidden md:block" />
            <BreadcrumbItem>
              {artefactKind === ArtefactKind.Inspection && reportId ? (
                <BreadcrumbLink asChild>
                  <NavLink to={`${prefix}${process.id}/${artefactKind}`}>
                    {artefactLabel[artefactKind]}
                  </NavLink>
                </BreadcrumbLink>
              ) : (
                <BreadcrumbPage className="text-muted-foreground font-normal">
                  {artefactLabel[artefactKind]}
                </BreadcrumbPage>
              )}
            </BreadcrumbItem>
          </>
        )}
        {artefactKind === ArtefactKind.Inspection && reportId && (
          <>
            <BreadcrumbSeparator className="hidden md:block" />
            <BreadcrumbItem>
              <BreadcrumbPage className="text-muted-foreground font-normal">
                <InspectionReportNumber artefactId={reportId} />
              </BreadcrumbPage>
            </BreadcrumbItem>
          </>
        )}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
